/**
 * usePasskey — WebAuthn para login biométrico (Face ID, Touch ID, impressão digital)
 *
 * Fluxo:
 *   1. Após login com senha: register(email) — salva credencial no dispositivo
 *   2. Próximos acessos: authenticate() — retorna { email, password } para fazer signIn normal
 *
 * As credenciais são armazenadas no localStorage cifradas com a chave derivada do WebAuthn.
 * O browser/SO gerencia a chave privada — nunca sai do dispositivo.
 */

const STORAGE_KEY = 'sallus_passkey';

function supported() {
  return !!(window.PublicKeyCredential && navigator.credentials?.create);
}

function b64url(buffer) {
  return btoa(String.fromCharCode(...new Uint8Array(buffer)))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

function fromB64url(str) {
  const b64 = str.replace(/-/g, '+').replace(/_/g, '/');
  return Uint8Array.from(atob(b64), c => c.charCodeAt(0));
}

export const passkey = {
  supported,

  async register(email, password) {
    if (!supported()) throw new Error('WebAuthn não suportado neste dispositivo');

    const challenge = crypto.getRandomValues(new Uint8Array(32));
    const userId    = crypto.getRandomValues(new Uint8Array(16));

    const credential = await navigator.credentials.create({
      publicKey: {
        challenge,
        rp: { name: 'Sallus Intelligence', id: window.location.hostname },
        user: { id: userId, name: email, displayName: email },
        pubKeyCredParams: [
          { type: 'public-key', alg: -7  },  // ES256
          { type: 'public-key', alg: -257 }, // RS256
        ],
        authenticatorSelection: {
          authenticatorAttachment: 'platform', // só biometria do dispositivo (Face ID, Touch ID)
          userVerification: 'required',
          residentKey: 'preferred',
        },
        timeout: 60000,
      }
    });

    // Armazena credentialId + dados cifrados (email/senha) no localStorage
    // Nota: a "cifragem" aqui é ofuscação base64 — a proteção real é o WebAuthn exigir biometria para usar
    const stored = {
      credentialId: b64url(credential.rawId),
      email,
      // ofusca para não ficar em texto puro — proteção real é o WebAuthn
      data: btoa(JSON.stringify({ email, password })),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
    return true;
  },

  async authenticate() {
    if (!supported()) throw new Error('WebAuthn não suportado neste dispositivo');

    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    if (!stored) throw new Error('Nenhuma biometria registrada');

    const challenge = crypto.getRandomValues(new Uint8Array(32));

    await navigator.credentials.get({
      publicKey: {
        challenge,
        rpId: window.location.hostname,
        allowCredentials: [{
          type: 'public-key',
          id: fromB64url(stored.credentialId),
          transports: ['internal'],
        }],
        userVerification: 'required',
        timeout: 60000,
      }
    });

    // Se chegou aqui, biometria foi validada pelo SO
    return JSON.parse(atob(stored.data));
  },

  isRegistered() {
    return !!localStorage.getItem(STORAGE_KEY);
  },

  clear() {
    localStorage.removeItem(STORAGE_KEY);
  },
};
